/**
 * Pyodide runner — lazy-loads Pyodide from CDN once per session.
 * Provides `runCode(code)` and `runTests({ code, visibleTests, hiddenTests })`.
 *
 * Tests are evaluated by importing the user code as `solution` (when the user
 * defines functions) and by passing captured stdout as `stdout_text` for
 * print-based lessons. Both are supported in parallel — whichever the test uses.
 */
const PYODIDE_VERSION = "0.27.7";
const PYODIDE_CDN = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/pyodide.js`;

let pyodidePromise = null;

async function loadPyodideOnce() {
  if (pyodidePromise) return pyodidePromise;

  pyodidePromise = (async () => {
    if (!window.loadPyodide) {
      await new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-pyodide]');
        if (existing) {
          existing.addEventListener('load', resolve);
          existing.addEventListener('error', () => reject(new Error('Pyodide script load failed')));
          return;
        }
        const s = document.createElement("script");
        s.src = PYODIDE_CDN;
        s.async = true;
        s.dataset.pyodide = "1";
        s.onload = resolve;
        s.onerror = () => reject(new Error("Failed to load Pyodide script"));
        document.head.appendChild(s);
      });
    }
    const py = await window.loadPyodide({
      indexURL: `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`,
    });
    return py;
  })();

  return pyodidePromise;
}

export async function ensurePyodide(onProgress) {
  if (onProgress) onProgress("loading");
  const py = await loadPyodideOnce();
  if (onProgress) onProgress("ready");
  return py;
}

export function isPyodideLoaded() {
  return !!(pyodidePromise && window.loadPyodide);
}

/**
 * Run user code and capture stdout/stderr.
 * Returns { stdout, stderr, error, durationMs }.
 */
export async function runCode(code) {
  const py = await loadPyodideOnce();
  const start = performance.now();
  let stdout = "";
  let stderr = "";
  let error = null;

  try {
    py.setStdout({ batched: (s) => { stdout += s + "\n"; } });
    py.setStderr({ batched: (s) => { stderr += s + "\n"; } });
    await py.runPythonAsync(code || "");
  } catch (e) {
    error = String(e?.message || e);
  } finally {
    py.setStdout(undefined);
    py.setStderr(undefined);
  }
  return {
    stdout: stdout.replace(/\n$/, ""),
    stderr: stderr.replace(/\n$/, ""),
    error,
    durationMs: Math.round(performance.now() - start),
  };
}

/**
 * Build a Python script that:
 *   1. runs the learner's code, capturing stdout into stdout_text
 *   2. exposes the learner's module as `solution`
 *   3. runs each test function, recording passed / failed with messages
 *
 * Returns { results: [{name, passed, message}], summary: {passed, total} }.
 */
async function runTestsBlock(userCode, testCode, label) {
  const py = await loadPyodideOnce();
  // Escape user code & tests for embedding in a python triple-quoted string.
  const esc = (s) => (s || "")
    .replace(/\\/g, "\\\\")
    .replace(/"""/g, '\\"\\"\\"');

  const harness = `
import sys, io, types, traceback, json, inspect

USER_CODE = """${esc(userCode)}"""
TEST_CODE = """${esc(testCode)}"""

# 1. Build a "solution" module from the user's code
solution_mod = types.ModuleType("solution")
sys.modules["solution"] = solution_mod
_buf = io.StringIO()
_old_out = sys.stdout
sys.stdout = _buf
_user_error = None
try:
    exec(USER_CODE, solution_mod.__dict__)
except Exception as _e:
    _user_error = traceback.format_exc()
sys.stdout = _old_out
stdout_text = _buf.getvalue()
solution_mod.stdout_text = stdout_text

# 2. Build a test module — inject a tiny pytest polyfill so generated lessons
#    that use @pytest.mark.parametrize / pytest.raises work in Pyodide.
_pytest_shim_src = """
import types, sys
_pt = types.ModuleType('pytest')

class _Mark:
    def parametrize(self, argnames, argvalues, ids=None):
        if isinstance(argnames, str):
            names = [n.strip() for n in argnames.split(',')]
        else:
            names = list(argnames)
        def deco(fn):
            fn._pylab_param_names = names
            fn._pylab_param_values = list(argvalues)
            existing = getattr(fn, '_pylab_param_decorators', [])
            existing.append((names, list(argvalues)))
            fn._pylab_param_decorators = existing
            return fn
        return deco

class _RaisesContext:
    def __init__(self, expected, match=None):
        self.expected = expected
        self.match = match
    def __enter__(self):
        return self
    def __exit__(self, exc_type, exc, tb):
        if exc_type is None:
            raise AssertionError(f'DID NOT RAISE {self.expected.__name__}')
        if not issubclass(exc_type, self.expected):
            return False
        if self.match is not None:
            import re
            if not re.search(self.match, str(exc)):
                raise AssertionError(f'pattern not found in exception: {exc}')
        return True

def _raises(expected, match=None):
    return _RaisesContext(expected, match)

def _approx(value, rel=None, abs=None):
    import builtins as _bi
    class _Approx:
        def __init__(self, v):
            self.v = v
            self.rel = rel
            self.abs = abs
        def __eq__(self, other):
            tol = self.abs if self.abs is not None else 1e-6
            return _bi.abs(other - self.v) <= tol
        def __repr__(self):
            return f'approx({self.v})'
    return _Approx(value)

def _fixture(*args, **kwargs):
    if len(args) == 1 and callable(args[0]):
        return args[0]
    def deco(fn): return fn
    return deco

def _skip(*a, **kw): pass
def _fail(reason=''):
    raise AssertionError(reason)

_pt.mark = _Mark()
_pt.raises = _raises
_pt.approx = _approx
_pt.fixture = _fixture
_pt.skip = _skip
_pt.fail = _fail
sys.modules['pytest'] = _pt
"""
exec(_pytest_shim_src, {})

test_mod = types.ModuleType("__pylab_tests__")
test_mod.stdout_text = stdout_text
test_mod.solution = solution_mod
_test_load_error = None
try:
    exec(TEST_CODE, test_mod.__dict__)
except Exception:
    _test_load_error = traceback.format_exc()

# 3. Discover test functions (any callable starting with 'test_')
def _empty_assert_msg(fn):
    """When an assert has no message, show the test source so the user can see what's being checked."""
    try:
        src = inspect.getsource(fn)
        return f"Assertion failed in {fn.__name__}. Here's what was being checked:\\n{src.strip()}"
    except Exception:
        return "Assertion failed"

results = []
if _user_error is not None:
    # All tests fail with the user's error
    for name in dir(test_mod):
        if name.startswith("test_") and callable(getattr(test_mod, name)):
            results.append({"name": name, "passed": False, "message": "Your code raised an error before tests could run:\\n" + _user_error.strip()})
    if not results:
        results.append({"name": "load", "passed": False, "message": "Your code raised an error:\\n" + _user_error.strip()})
elif _test_load_error is not None:
    results.append({"name": "test_setup", "passed": False, "message": "Test loading failed: " + _test_load_error.strip()})
else:
    import itertools
    for name in dir(test_mod):
        if not name.startswith("test_"):
            continue
        fn = getattr(test_mod, name)
        if not callable(fn):
            continue
        # Unroll @pytest.mark.parametrize stacks (could be multiple decorators)
        decos = list(reversed(getattr(fn, "_pylab_param_decorators", [])))
        if decos:
            # Cartesian product of stacked parametrize layers.
            value_lists = [d[1] for d in decos]
            name_lists = [d[0] for d in decos]
            for combo in itertools.product(*value_lists):
                kwargs = {}
                case_label_parts = []
                for layer_idx, vals in enumerate(combo):
                    layer_names = name_lists[layer_idx]
                    if len(layer_names) == 1:
                        kwargs[layer_names[0]] = vals
                        case_label_parts.append(repr(vals))
                    else:
                        if not isinstance(vals, (list, tuple)):
                            vals = (vals,)
                        for nm, vv in zip(layer_names, vals):
                            kwargs[nm] = vv
                        case_label_parts.append(",".join(repr(v) for v in vals))
                case_name = name + "[" + " | ".join(case_label_parts) + "]"
                try:
                    fn(**kwargs)
                    results.append({"name": case_name, "passed": True, "message": "ok"})
                except AssertionError as _e:
                    msg = str(_e) or _empty_assert_msg(fn)
                    results.append({"name": case_name, "passed": False, "message": msg})
                except Exception:
                    results.append({"name": case_name, "passed": False, "message": traceback.format_exc().strip()})
            continue

        # Non-parametrized: inject standard fixtures by name.
        try:
            sig = inspect.signature(fn)
            kwargs = {}
            for p in sig.parameters.values():
                if p.name == "stdout_text":
                    kwargs[p.name] = stdout_text
                elif p.name == "solution":
                    kwargs[p.name] = solution_mod
            fn(**kwargs)
            results.append({"name": name, "passed": True, "message": "ok"})
        except AssertionError as _e:
            msg = str(_e) or _empty_assert_msg(fn)
            results.append({"name": name, "passed": False, "message": msg})
        except Exception:
            results.append({"name": name, "passed": False, "message": traceback.format_exc().strip()})

passed = sum(1 for r in results if r["passed"])
_payload = {"label": "${label}", "results": results, "passed": passed, "total": len(results), "stdout": stdout_text}
json.dumps(_payload)
`;
  const jsonStr = await py.runPythonAsync(harness);
  return JSON.parse(jsonStr);
}

/**
 * Run both visible & hidden tests in sequence. Returns a unified report:
 * { verdict, visible: {results, passed, total}, hidden: {...}, stdout, durationMs }.
 */
export async function runTests({ code, visibleTests, hiddenTests }) {
  await loadPyodideOnce();
  const start = performance.now();
  const visible = visibleTests
    ? await runTestsBlock(code, visibleTests, "visible")
    : { results: [], passed: 0, total: 0, stdout: "" };
  const hidden = hiddenTests
    ? await runTestsBlock(code, hiddenTests, "hidden")
    : { results: [], passed: 0, total: 0, stdout: visible.stdout || "" };

  const totalAll = visible.total + hidden.total;
  const passedAll = visible.passed + hidden.passed;
  let verdict;
  if (totalAll === 0) {
    verdict = "ERROR";
  } else if (passedAll === totalAll) {
    verdict = "PASS";
  } else if (passedAll === 0) {
    verdict = "FAIL";
  } else {
    verdict = "PARTIAL";
  }
  return {
    verdict,
    visible,
    hidden,
    stdout: visible.stdout || hidden.stdout || "",
    durationMs: Math.round(performance.now() - start),
    passedAll,
    totalAll,
  };
}
